import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Utility } from 'projects/Autofacets-Core/src/app/utils/utility';
import * as Enums from './../../../../../../Autofacets-Core/src/app/utils/enums';
import {
  MechanicCustomerWaitingDayRow, MechanicCustomerWaitingOccasionViewModel, MechanicCustomerWaitingResultModel,
  MechanicCustomerWaitingSlotSaveItem
} from '../../../business-entities/mechanic-customer-waiting-hours-view-model';
import { DealerWorkingHoursBusinessService } from '../../../business-services/dealer-working-hours.service';
import { MechanicService } from '../../../business-services/mechanic.service';

const GeneralShiftOccasionId = 1;
const ResultMessages: { [code: number]: string } = {
  [-1]: 'lblClashingDate',
  [-4]: 'lblMechanicCWSlotLocked',
  [-5]: 'lblEndDateGreaterOrEqualToStartDate',
  [-6]: 'lblPlanningDoneSpecialOccasionNotDeleted',
  [-7]: 'lblMechanicCWSlotLocked',
  [-8]: 'lblTimeSlotClashing',
  [-9]: 'lblSlotTimeError',
  [-10]: 'lblRecordNotFound'
};

@Component({
  selector: 'app-edit-customer-waiting-hours-mechanic',
  templateUrl: './edit-customer-waiting-hours-mechanic.component.html',
  styleUrls: ['./edit-customer-waiting-hours-mechanic.component.css']
})
export class EditCustomerWaitingHoursMechanicComponent implements OnInit {
  mechanicId: any;
  dealerId: any;
  occasionForm: FormGroup;
  startdate: string;
  enddate: string;
  minDate: string;
  occasionList: MechanicCustomerWaitingOccasionViewModel[] = [];
  selectedOccasion: MechanicCustomerWaitingOccasionViewModel = null;
  selectedRadioButton: number = 0;
  previousRadioButton: number = 0;
  days: MechanicCustomerWaitingDayRow[] = [];
  deletedSlots: MechanicCustomerWaitingSlotSaveItem[] = [];
  slotFrom: any = { hour: 8, minute: 0 };
  slotTill: any = { hour: 9, minute: 0 };
  isUpdatedValue: boolean = false;
  private dealerShift: { [day: number]: string } = {};
  private mechanicShift: { [day: number]: string } = {};

  constructor(
    private service: MechanicService,
    private dealerWorkingHoursService: DealerWorkingHoursBusinessService,
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private activeModal: NgbActiveModal,
    private cdrf: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this.mechanicId = this.service.mechanicId;
    this.dealerId = this.service.dealerId;
    this.minDate = moment().format('YYYY/MM/DD');
    this.occasionForm = this.formBuilder.group({
      occasionname: ['', [Validators.required]],
      startdate: ['', [Validators.required]],
      enddate: ['', [Validators.required]]
    });
    await this.getShiftDetails();
    this.getOccasionList();
  }

  get cpf() {
    return this.occasionForm.controls;
  }

  get isReadOnly(): boolean {
    return !!this.selectedOccasion?.isPast;
  }

  get isStartDateLocked(): boolean {
    return !!this.selectedOccasion && (this.selectedOccasion.isPast || this.selectedOccasion.isRunning);
  }

  async getShiftDetails() {
    const dealerHours = await this.dealerWorkingHoursService.getDealerWorkingHoursDetails(this.dealerId, GeneralShiftOccasionId);
    (dealerHours || []).forEach(x => {
      this.dealerShift[x.dayNumber] = x.open ? this.formatShift(x.openingTimeHrs, x.closingTimeHrs) : 'lblClosed';
    });
    const result = await this.service.GetMechanicOpeningHoursById(this.mechanicId, GeneralShiftOccasionId);
    if (result.responseCode === Enums.ErrorCodes.SuccessCode) {
      result.responseObject.forEach(x => {
        this.mechanicShift[x.day] = x.isActive ? this.formatShift(x.openingTimeHrsDate, x.closingTimeHrsDate) : 'lblClosed';
      });
    }
  }

  async getOccasionList(selectId: number = 0) {
    const result = await this.service.GetMechanicCustomerWaitingOccasions(this.dealerId);
    this.occasionList = result.responseObject ?? [];
    const occasion = this.occasionList.find(x => x.id === selectId) ?? this.occasionList.find(x => !x.isPast) ?? this.occasionList[0];
    if (occasion) {
      this.selectedRadioButton = occasion.id;
      this.loadOccasionDetails();
    } else {
      this.OnClear();
    }
  }

  ChangeOccasion() {
    if (this.isUpdatedValue) {
      this.toastr.warning(this.translate.instant('lblSaveOccasionChange'));
      setTimeout(() => this.selectedRadioButton = this.previousRadioButton, 0);
      return;
    }
    this.loadOccasionDetails();
  }

  loadOccasionDetails() {
    this.previousRadioButton = this.selectedRadioButton;
    this.selectedOccasion = this.occasionList.find(x => x.id === this.selectedRadioButton);
    this.startdate = Utility.GetFormattedDate(this.selectedOccasion.startDate);
    this.enddate = Utility.GetFormattedDate(this.selectedOccasion.endDate);
    this.occasionForm.reset({
      occasionname: this.selectedOccasion.occasionName,
      startdate: this.startdate,
      enddate: this.enddate
    });
    this.cdrf.detectChanges();
    this.getWaitingSlots();
  }

  async getWaitingSlots() {
    this.spinner.show();
    const result = await this.service.GetMechanicCustomerWaitingSlots(this.mechanicId, this.selectedRadioButton);
    this.spinner.hide();
    const slots = result.responseObject ?? [];
    this.days = [0, 1, 2, 3, 4, 5, 6].map(day => {
      const daySlots = slots.filter(x => x.day === day);
      return {
        day: day,
        dayText: this.service.getDayText(day),
        isActive: daySlots.length ? daySlots.some(x => x.isActive) : true,
        isClosed: this.dealerShift[day] === 'lblClosed' || this.mechanicShift[day] === 'lblClosed',
        isSelected: false,
        dealerShiftDetails: this.dealerShift[day] ?? '-',
        mechanicShiftDetails: this.mechanicShift[day] ?? '-',
        slots: daySlots.map(x => ({
          id: x.id,
          startTime: Utility.ConvertToTimeFormat(x.startTimeDate).slice(0, 5),
          endTime: Utility.ConvertToTimeFormat(x.endTimeDate).slice(0, 5),
          isLocked: x.isLocked
        }))
      };
    });
    this.deletedSlots = [];
    this.isUpdatedValue = false;
  }

  get hasSelectedDay(): boolean {
    return this.days.some(x => x.isSelected && x.isActive && !x.isClosed);
  }

  onTimeSlotAdd() {
    const selectedDays = this.days.filter(x => x.isSelected && x.isActive && !x.isClosed);
    if (selectedDays.length === 0) {
      return;
    }
    const startTime = moment().hours(this.slotFrom.hour).minutes(this.slotFrom.minute).format('HH:mm');
    const endTime = moment().hours(this.slotTill.hour).minutes(this.slotTill.minute).format('HH:mm');
    if (moment(endTime, 'HH:mm').diff(moment(startTime, 'HH:mm'), 'minutes') < 15) {
      this.toastr.error(this.translate.instant('lblSlotTimeError'));
      return;
    }
    const clashingDays = [];
    selectedDays.forEach(day => {
      if (day.slots.some(x => startTime < x.endTime && x.startTime < endTime)) {
        clashingDays.push(day.dayText);
        return;
      }
      day.slots.push({ id: 0, startTime: startTime, endTime: endTime, isLocked: false });
      day.slots.sort((a, b) => a.startTime.localeCompare(b.startTime));
      this.isUpdatedValue = true;
    });
    if (clashingDays.length > 0) {
      this.toastr.error(this.translate.instant('lblTimeSlotClashing') + ' ' + clashingDays.join(', '));
    }
  }

  onTimeSlotDelete(day: MechanicCustomerWaitingDayRow, index: number) {
    const slot = day.slots[index];
    if (slot.id > 0) {
      this.deletedSlots.push(this.toSaveItem(day, slot, true));
    }
    day.slots.splice(index, 1);
    this.isUpdatedValue = true;
  }

  async onSaveChanges(isSaveAndClose: boolean) {
    const slots = [...this.deletedSlots];
    this.days.forEach(day => day.slots.forEach(slot => slots.push(this.toSaveItem(day, slot, false))));
    this.spinner.show();
    const result = await this.service.SaveMechanicCustomerWaitingSlots({
      mechanicId: this.mechanicId,
      occasionId: this.selectedRadioButton,
      slots: slots
    });
    this.spinner.hide();
    if (!this.showResult(result.responseObject, 'lblMechanicCustomerWaitingSlotsAdded')) {
      return;
    }
    if (isSaveAndClose) {
      this.activeModal.close({ success: true });
    } else {
      this.getWaitingSlots();
    }
  }

  async AddOccasionDetails() {
    this.occasionForm.markAllAsTouched();
    if (this.occasionForm.invalid) {
      return;
    }
    const startDate = Utility.getDateStringToDate(this.occasionForm.value.startdate);
    const endDate = Utility.getDateStringToDate(this.occasionForm.value.enddate);
    if (endDate < startDate) {
      this.toastr.error(this.translate.instant('lblEndDateGreaterOrEqualToStartDate'));
      return;
    }
    const result = await this.service.AddUpdateMechanicCustomerWaitingOccasion({
      id: this.selectedRadioButton,
      dealerId: this.dealerId,
      occasionName: this.occasionForm.value.occasionname,
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD')
    });
    if (this.showResult(result.responseObject, 'lblAddSuccessfully')) {
      this.isUpdatedValue = false;
      this.getOccasionList(result.responseObject.occasionId);
    }
  }

  OnClear() {
    if (this.isUpdatedValue) {
      this.toastr.warning(this.translate.instant('lblSaveOccasionChange'));
      return;
    }
    this.selectedRadioButton = 0;
    this.previousRadioButton = 0;
    this.selectedOccasion = null;
    this.startdate = null;
    this.enddate = null;
    this.days = [];
    this.occasionForm.reset({ occasionname: '', startdate: null, enddate: null });
  }

  async OnDelete() {
    const result = await this.service.DeleteMechanicCustomerWaitingOccasion({ id: this.selectedRadioButton, dealerId: this.dealerId });
    if (this.showResult(result.responseObject, 'lblSuccesfullyDeletedSpecialOccasion')) {
      this.isUpdatedValue = false;
      this.getOccasionList();
    }
  }

  OnClose() {
    this.activeModal.close({ success: true });
  }

  private showResult(response: MechanicCustomerWaitingResultModel, successLabel: string): boolean {
    if (response?.resultCode === 1) {
      this.toastr.success(this.translate.instant(successLabel));
      return true;
    }
    this.toastr.error(this.translate.instant(ResultMessages[response?.resultCode] ?? 'lblErrorOccured'));
    return false;
  }

  private toSaveItem(day: MechanicCustomerWaitingDayRow, slot: any, isDeleted: boolean): MechanicCustomerWaitingSlotSaveItem {
    return {
      id: slot.id,
      day: day.day,
      startTimeDate: Utility.ConvertLocalTimeToDateTime(slot.startTime),
      endTimeDate: Utility.ConvertLocalTimeToDateTime(slot.endTime),
      // A closed day stores its old slots as inactive, unless one is booked (booked slots cannot change)
      isActive: day.isActive && (!day.isClosed || day.slots.some(x => x.isLocked)),
      isDeleted: isDeleted
    };
  }

  private formatShift(openingTime: any, closingTime: any): string {
    const open = openingTime ? Utility.ConvertToTimeFormat(openingTime).slice(0, 5) : '00:00';
    const close = closingTime ? Utility.ConvertToTimeFormat(closingTime).slice(0, 5) : '00:00';
    return open + '-' + close;
  }
}
